import React, { useRef, useState } from 'react';
import { IMAGE_UPLOAD_CONFIG } from '@/utils/imageUploadConfig';
import type { ImageUploadType } from '@/utils/imageUploadConfig';

interface ImageUploaderProps {
  type: ImageUploadType;
  onUpload: (file: File, dataUrl: string) => void;
  className?: string;
  buttonText?: string;
  currentImage?: string | null;
}

const MIME_MAP: Record<string, string> = {
  'JPG': 'image/jpeg',
  'JPEG': 'image/jpeg',
  'PNG': 'image/png',
  'WEBP': 'image/webp',
  'SVG': 'image/svg+xml',
};

export default function ImageUploader({ 
  type, 
  onUpload, 
  className = '', 
  buttonText = 'Upload Image',
  currentImage
}: ImageUploaderProps) {
  const config = IMAGE_UPLOAD_CONFIG[type] || IMAGE_UPLOAD_CONFIG['custom'] || {
    type: 'custom' as ImageUploadType,
    label: 'Image',
    width: 1200,
    height: 800,
    aspectRatio: '16:9',
    formats: ['JPG', 'PNG', 'WEBP', 'JPEG'],
    maxSizeMB: 5,
    guidance: 'Upload a high-quality image file.',
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const acceptString = config.formats
    .map(fmt => MIME_MAP[fmt.toUpperCase()])
    .filter(Boolean)
    .join(',');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setWarning(null);
    
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. File size validation
    const maxBytes = config.maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File exceeds the maximum size of ${config.maxSizeMB} MB. Please upload a smaller image.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // 2. Format validation
    const fileExt = file.name.split('.').pop()?.toUpperCase() || '';
    const isSvg = fileExt === 'SVG' || file.type === 'image/svg+xml';
    
    const isSupportedFormat = config.formats.some(fmt => 
      fmt.toUpperCase() === fileExt || 
      (fmt.toUpperCase() === 'JPG' && fileExt === 'JPEG') ||
      MIME_MAP[fmt.toUpperCase()] === file.type
    );

    if (!isSupportedFormat) {
      setError(`Unsupported format. Allowed formats: ${config.formats.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // 3. Read File & Dimension Validation
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      if (!dataUrl) return;

      // Check dimensions if it's not an SVG
      if (!isSvg) {
        const img = new Image();
        img.onload = () => {
          // Warning if dimensions are significantly smaller (e.g. less than 80% of recommended)
          const threshold = 0.8;
          if (img.width < config.width * threshold || img.height < config.height * threshold) {
            setWarning('This image is smaller than the recommended size and may appear blurry on larger screens.');
          }
          // Proceed with upload anyway
          onUpload(file, dataUrl);
        };
        img.onerror = () => {
          // If image fails to load, just proceed (might be a weird format)
          onUpload(file, dataUrl);
        };
        img.src = dataUrl;
      } else {
        // SVGs don't need dimension validation in the same way
        onUpload(file, dataUrl);
      }
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isIconType = type === 'customIcon' || type === 'icon';

  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
        <div className="flex-shrink-0 space-y-2">
          <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer shadow-sm">
            <i className="ri-upload-cloud-2-line text-gray-500 text-lg"></i>
            {currentImage ? (isIconType ? 'Change Icon' : 'Change Image') : buttonText}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptString}
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
        </div>

        <div className="flex-1 space-y-2 text-xs text-gray-600">
          <div className="flex items-center gap-1.5 mb-1">
            <i className={`${isIconType ? 'ri-remixicon-line text-teal-600' : 'ri-image-line text-gray-400'}`}></i>
            <span className="font-semibold text-gray-800">{config.label} Requirements</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            <p><span className="text-gray-400">Recommended:</span> <strong className="text-gray-700">{config.width} × {config.height} px</strong></p>
            <p><span className="text-gray-400">Aspect Ratio:</span> {config.aspectRatio}</p>
            <p><span className="text-gray-400">Max Size:</span> {config.maxSizeMB} MB</p>
            <p><span className="text-gray-400">Formats:</span> {config.formats.join(', ')}</p>
          </div>
          <p className="text-gray-500 pt-1 italic">{config.guidance}</p>
        </div>
      </div>

      {(error || warning) && (
        <div className="pt-2 border-t border-gray-200/50">
          {error && (
            <p className="text-xs text-red-600 flex items-start gap-1.5 bg-red-50 p-2 rounded-md">
              <i className="ri-error-warning-fill mt-0.5"></i>
              <span>{error}</span>
            </p>
          )}
          {warning && (
            <p className="text-xs text-amber-700 flex items-start gap-1.5 bg-amber-50 p-2 rounded-md">
              <i className="ri-alert-fill mt-0.5"></i>
              <span>{warning}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
